import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, ChevronLeft, TrendingDown, TrendingUp } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import {
  getEngageStatusBadge, getEngageSourceBadge,
  ENGAGE_STATUS_LABEL, ENGAGE_SOURCE_LABEL,
} from '../../../utils/engageStageBadge';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { presetToRange } from './ReportFilters';


export default function EnquiryFunnelReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { from, to } = presetToRange(preset, customFrom, customTo);
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to)   q.set('to', to);
        const qs = q.toString();
        const resp = await apiFetch(`engage/analytics/enquiry-funnel/${qs ? '?' + qs : ''}`);
        if (resp?.status === 'fail') setError(resp.message || 'Failed to load report');
        setData(resp?.status === 'fail' ? null : resp);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, reloadTick]);

  // The API groups by source; each group carries its own status counts. The
  // stage funnel is those counts summed across sources.
  const groups = data?.groups || [];
  const FUNNEL_STATUSES = ['new', 'contacted', 'appointment_booked', 'converted', 'dropped'];
  const stageBuckets = FUNNEL_STATUSES.map(status => ({
    status,
    count: groups.reduce((n, g) => n + (Number(g.by_status?.[status]) || 0), 0),
  }));
  const bySource = groups.map(g => ({
    source: g.group_key,
    total: g.total,
    converted: g.by_status?.converted || 0,
    dropped: g.by_status?.dropped || 0,
  }));
  const total = data?.total || 0;
  const converted = stageBuckets.find(b => b.status === 'converted').count;
  const conversionPct = total ? Math.round((converted / total) * 100) : 0;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
          <PieChart className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Enquiry Funnel</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Source × stage breakdown with drop-off</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div> : (
        <>
          {/* Headline */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Total enquiries</div>
              <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{total}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Converted</div>
              <div className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">{converted}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Overall conversion</div>
              <div className="text-2xl font-bold mt-1 text-indigo-600 dark:text-indigo-400">{conversionPct}%</div>
            </div>
          </div>

          {/* Stage funnel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Stage funnel</h2>
            <div className="space-y-2">
              {stageBuckets.map((row, i) => {
                const pct = total ? Math.round(row.count / total * 100) : 0;
                const dropFromPrev = i > 0
                  ? stageBuckets[i-1].count - row.count : 0;
                return (
                  <div key={row.status} className="flex items-center gap-3">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[11px] font-medium min-w-[130px]', getEngageStatusBadge(row.status))}>
                      {ENGAGE_STATUS_LABEL[row.status] || row.status}
                    </span>
                    <div className="flex-1 relative">
                      <div className="h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all"
                             style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="w-16 text-right">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">{row.count}</div>
                      <div className="text-[10px] text-gray-500">{pct}%</div>
                    </div>
                    {i > 0 && dropFromPrev > 0 && (
                      <div className="text-[10px] text-red-500 inline-flex items-center gap-0.5 w-16">
                        <TrendingDown className="w-3 h-3" /> -{dropFromPrev}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* By source */}
          <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">By source</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Source</th>
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                  <th className="px-4 py-2 text-right font-medium">Converted</th>
                  <th className="px-4 py-2 text-right font-medium">Conv. %</th>
                  <th className="px-4 py-2 text-right font-medium">Dropped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {bySource.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No source data yet.</td></tr>
                ) : bySource.map(row => {
                  const convPct = row.total ? Math.round((row.converted / row.total) * 100) : 0;
                  return (
                    <tr key={row.source} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-2.5">
                        <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(row.source))}>
                          {ENGAGE_SOURCE_LABEL[row.source] || row.source}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-white">{row.total}</td>
                      <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400 font-medium">{row.converted}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={cn(
                          'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                          convPct >= 30 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                          convPct >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        )}>
                          {convPct}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-red-500 font-medium">{row.dropped || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
